import { Pause, Play, SkipForward, Mic, X, Volume2, Loader2, AudioLines } from "lucide-react";
import { t } from "@/lib/i18n";
import { ClaraAvatar } from "./Brand";

const PHASE = {
  speaking: { Icon: Volume2, k: "guide.speaking" },
  listening: { Icon: Mic, k: "guide.listeningLbl" },
  thinking: { Icon: Loader2, k: "guide.thinkingLbl", spin: true },
  idle: { Icon: AudioLines, k: "guide.idleLbl" },
};

const Btn = ({ testId, onClick, label, children, primary }) => (
  <button data-testid={testId} onClick={onClick} aria-label={label} title={label}
    className={`flex h-12 min-w-12 items-center justify-center gap-2 rounded-full px-4 text-xs font-bold uppercase tracking-[0.1em] transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-ac-primary-ring ${primary ? "bg-ac-primary text-white hover:bg-ac-primary-hover" : "border border-white/20 text-white hover:bg-white/10"}`}>
    {children}
  </button>
);

export const VoiceGuideBar = ({ guide }) => {
  if (!guide.active) return null;
  const p = guide.paused ? { Icon: Pause, k: "guide.paused" } : PHASE[guide.phase] || PHASE.idle;
  return (
    <div data-testid="voice-guide-bar" role="region" aria-label={t("guide.on")}
      className="hg-rise fixed inset-x-3 bottom-3 z-50 mx-auto max-w-4xl rounded-3xl border border-white/10 bg-ac-glass p-4 text-white shadow-2xl backdrop-blur-xl sm:bottom-6">
      {guide.needsGesture && (
        <button data-testid="voice-guide-tap-to-start" className="mb-3 flex w-full items-center justify-center gap-3 rounded-2xl bg-ac-primary px-4 py-4 text-base font-bold text-white">
          <Volume2 className="h-6 w-6 animate-pulse" /> {t("guide.tapToStart")}
        </button>
      )}
      <div className="flex items-start gap-3">
        <ClaraAvatar size="h-12 w-12" online={!guide.paused} ring="border-[#03060d]" />
        <div className="min-w-0 flex-1">
          <p data-testid="voice-guide-phase" className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-ac-sky">
            <p.Icon className={`h-3.5 w-3.5 ${p.spin ? "animate-spin" : ""} ${guide.phase === "listening" && !guide.paused ? "animate-pulse text-red-400" : ""}`} /> {t(p.k)}
          </p>
          <p data-testid="voice-guide-caption" aria-live="polite" className="mt-1 line-clamp-3 text-sm leading-relaxed text-white/90">{guide.caption}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Btn testId="voice-guide-pause" onClick={guide.togglePause} label={guide.paused ? t("guide.resume") : t("guide.pause")} primary>
          {guide.paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />} {guide.paused ? t("guide.resume") : t("guide.pause")}
        </Btn>
        <Btn testId="voice-guide-skip" onClick={guide.skip} label={t("guide.skip")}><SkipForward className="h-4 w-4" /><span className="hidden sm:inline">{t("guide.skip")}</span></Btn>
        <Btn testId="voice-guide-talk" onClick={guide.talk} label={t("guide.talk")}><Mic className="h-4 w-4" /><span className="hidden sm:inline">{t("guide.talk")}</span></Btn>
        <span className="ml-auto hidden font-mono text-[10px] text-white/50 xl:block">{t("guide.shortcuts")}</span>
        <Btn testId="voice-guide-exit" onClick={() => guide.stop(true)} label={t("guide.autoOff")}><X className="h-4 w-4" /> {t("guide.autoOff")}</Btn>
      </div>
    </div>
  );
};
